import React, { createContext, useContext, useMemo } from 'react';

export interface BrandTheme {
  name: string;
  displayName: string;
  phoneNumber: string;
  logo: string;
  homeBackgroundImage: string;
  formBackgroundImage: string;
  warrantyCard?: string;
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    background: string;
    text: string;
  };
  fonts?: {
    primary?: string;
    secondary?: string;
  };
  /**
   * URLs des pages légales. Si défini, le footer utilise ces paths/URLs ;
   * sinon, fallback sur `${env.DECLARATION_SAV_URL}/legal_notice` etc.
   */
  legalLinks?: {
    legalNotice: string;
    privacyPolicy: string;
    termsAndConditions: string;
  };
}

export type BrandType = 'apollo' | 'linglong' | 'vulco' | 'dewillermin';

const brandThemes: Record<BrandType, BrandTheme> = {
  apollo: {
    name: 'apollo',
    displayName: 'Apollo',
    phoneNumber: '09 78 45 20 41',
    logo: '/assets/logos/apollo-logo.png',
    homeBackgroundImage: '/assets/images/home-background-apollo.png',
    formBackgroundImage: '/assets/images/form-background-apollo.png',
    warrantyCard: '/assets/images/warranty-card-apollo.png',
    colors: {
      primary: '#5D2482', // Violet Apollo
      secondary: '#5A2D6F', // Violet plus foncé
      accent: '#9D5FB7', // Violet plus clair
      background: '#ffffff',
      text: '#333333'
    }
  },
  linglong: {
    name: 'linglong',
    displayName: 'Linglong',
    phoneNumber: '09 78 45 28 34',
    logo: '/assets/logos/linglong-logo.png', 
    homeBackgroundImage: '/assets/images/home-background-linglong.png',
    formBackgroundImage: '/assets/images/form-background-linglong.png',
    warrantyCard: '/assets/images/warranty-card-linglong.png',
    colors: {
      primary: '#2347A3', // Bleu Linglong
      secondary: '#002A74', // Bleu plus foncé
      accent: '#0052CC', // Bleu plus clair
      background: '#ffffff',
      text: '#333333'
    }
  },
  vulco: {
    name: 'vulco',
    displayName: 'vulco',
    phoneNumber: '09 78 45 59 55',
    logo: '/assets/logos/vulco-logo-2022.svg',
    homeBackgroundImage: '/assets/images/home-background-vulco.png',
    formBackgroundImage: '/assets/images/form-background-vulco.png',
    warrantyCard: '/assets/images/warranty-card-vulco.png',
    colors: {
      primary: '#002855', // Bleu vulco
      secondary: '#FFCD00', // Jaune vulco
      accent: '#0052CC', // Bleu plus clair
      background: '#ffffff',
      text: '#333333'
    }
  },
  dewillermin: {
    name: 'dewillermin',
    displayName: 'de Willermin',
    phoneNumber: '04 12 28 21 27',
    logo: '/assets/logos/dewillermin-logo.png',
    homeBackgroundImage: '/assets/images/home-background-dewillermin.png',
    formBackgroundImage: '/assets/images/form-background-dewillermin.png',
    colors: {
      primary: '#000000',
      secondary: '#CB8342',
      accent: '#4A90C2',
      background: '#ffffff',
      text: '#000000'
    },
    fonts: {
      primary: 'Outfit, sans-serif'
    },
    legalLinks: {
      legalNotice: '/legal-notice',
      privacyPolicy: '/privacy-policy',
      termsAndConditions: '/terms-and-conditions'
    }
  }
};

interface BrandThemeContextType {
  brand: BrandType;
  theme: BrandTheme;
}

const BrandThemeContext = createContext<BrandThemeContextType | undefined>(undefined);

export const getBrandFromHostname = (hostname: string): BrandType => {
  if (hostname.includes('apollo')) return 'apollo';
  if (hostname.includes('linglong')) return 'linglong';
  if (hostname.includes('vulco')) return 'vulco';
  if (hostname.includes('dewillermin')) return 'dewillermin';
  return 'apollo'; // default
};

interface BrandThemeProviderProps {
  children: React.ReactNode;
  forceBrand?: BrandType;
}

export const BrandThemeProvider: React.FC<BrandThemeProviderProps> = ({ children, forceBrand }) => {
  const brand = useMemo(() => {
    if (forceBrand) return forceBrand;
    return getBrandFromHostname(window.location.hostname);
  }, [forceBrand]);

  const theme = brandThemes[brand];

  const value = useMemo(() => ({
    brand,
    theme
  }), [brand, theme]);

  return (
    <BrandThemeContext.Provider value={value}>
      <div className={`brand-theme-${brand}`}>
        {children}
      </div>
    </BrandThemeContext.Provider>
  );
};

export const useBrandTheme = (): BrandThemeContextType => {
  const context = useContext(BrandThemeContext);
  if (!context) {
    throw new Error('useBrandTheme must be used within a BrandThemeProvider');
  }
  return context;
};

// Helper hook to get CSS variables for the current theme
export const useBrandCSSVariables = () => {
  const { theme } = useBrandTheme();
  
  return {
    '--brand-primary': theme.colors.primary,
    '--brand-secondary': theme.colors.secondary,
    '--brand-accent': theme.colors.accent,
    '--brand-background': theme.colors.background,
    '--brand-text': theme.colors.text,
  } as React.CSSProperties;
};